import React from "react";

import { ReactComponent as WarningIcon } from "@common/assets/images/warning.svg";
import HorizontalBar, { HorizontalBarItem } from "@common/components/HorizontalBar/HorizontalBar";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { TestingCoverage, TestingCoverageItem } from "@common/models/Analytics.model";
import { getDimensionMap } from "@main/pages/Release/helpers/dimensionMapper";
import { getMetricDimensionConfig } from "@main/pages/Release/helpers/metricDimensionConfig";
import { ReleaseMetricCard, ReleaseMetricCardBaseProps } from "@main/pages/Release/views/MetricCards/ReleaseMetricCard";
import * as Styled from "@main/pages/Release/views/MetricCards/TestingCoverageCard.styles";

interface TestingCoverageCardProps extends ReleaseMetricCardBaseProps {
  data?: TestingCoverage;
}

const TestingCoverageCard = ({ style, data, score, highlight, error, onMoreClick }: TestingCoverageCardProps) => {
  const isEmpty =
    Object.keys(data?.tickets ?? {}).length === 0 &&
    Object.keys(data?.sessions?.dimensions ?? {}).length === 0 &&
    Object.keys(data?.sessions?.metadata ?? {}).length === 0;

  const metrics = data ? buildMetrics(data) : [];
  const rowCount = Math.ceil(Math.max(metrics.length, 1) / 3);

  return (
    <ReleaseMetricCard
      style={style}
      title={"Coverage"}
      score={score}
      highlight={highlight}
      error={error}
      onMoreClick={onMoreClick}
    >
      {!isEmpty ? (
        <Styled.MetricsGrid $rowCount={rowCount}>
          {metrics.map((metric) => (
            <TestingCoverageMetric
              key={metric.key}
              topBarItems={metric.topBarItems}
              bottomBarItems={metric.bottomBarItems}
              icon={metric.icon}
              text={metric.label}
              hasWarning={metric.hasWarning}
            />
          ))}
        </Styled.MetricsGrid>
      ) : (
        <Paragraph
          style={{ textAlign: "center" }}
          p2
        >
          {"No sessions recorded yet for this release."}
        </Paragraph>
      )}
    </ReleaseMetricCard>
  );
};

interface TestingCoverageMetricProps {
  topBarItems: HorizontalBarItem[];
  bottomBarItems: HorizontalBarItem[];
  icon: React.ReactNode;
  text: string;
  hasWarning?: boolean;
}

const TestingCoverageMetric = ({ topBarItems, bottomBarItems, icon, text, hasWarning }: TestingCoverageMetricProps) => {
  return (
    <Styled.MetricRow>
      <Styled.BarsColumn>
        <HorizontalBar data={topBarItems} />
        <HorizontalBar
          style={{ height: "1rem" }}
          data={bottomBarItems}
        />
      </Styled.BarsColumn>
      <Styled.BarsDetails>
        <Styled.IconWrapper>{icon}</Styled.IconWrapper>
        {hasWarning && (
          <Styled.WarningIconWrapper>
            <WarningIcon />
          </Styled.WarningIconWrapper>
        )}
        <Styled.MetricLabel>{text}</Styled.MetricLabel>
      </Styled.BarsDetails>
    </Styled.MetricRow>
  );
};

interface MetricEntry {
  key: string;
  label: string;
  icon: React.ReactNode;
  topBarItems: HorizontalBarItem[];
  bottomBarItems: HorizontalBarItem[];
  hasWarning: boolean;
}

function buildMetrics(data: TestingCoverage): MetricEntry[] {
  const tickets = getDimensionMap(data.tickets);
  const sessions = getDimensionMap(data.sessions);

  const keys = Array.from(new Set([...Object.keys(tickets), ...Object.keys(sessions)]));

  return keys.map((key) => {
    let ticketItems = tickets[key]?.items ?? [];
    ticketItems = ticketItems.filter((i) => i.count > 0);

    let sessionItems = sessions[key]?.items ?? [];
    sessionItems = sessionItems.filter((i) => i.count > 0);

    const config = getMetricDimensionConfig(key);

    return {
      key,
      label: config.label,
      icon: config.icon,
      topBarItems: toHorizontalBarItems(ticketItems, TOP_BAR_COLORS, "Tickets breakdown:"),
      bottomBarItems: toHorizontalBarItems(sessionItems, BOTTOM_BAR_COLORS, "Sessions breakdown:"),
      hasWarning: false,
    };
  });
}

const TOP_BAR_COLORS = ["#7B61FF", "#9580FF", "#AB99FF", "#C4B8FF", "#D9D0FF"];
const BOTTOM_BAR_COLORS = ["#38B2D8", "#62C9E5", "#8ADDF0", "#AEEDFA", "#D0F6FF"];

function toHorizontalBarItems(
  items: TestingCoverageItem[],
  colors: string[],
  tooltipPrefix: string,
): HorizontalBarItem[] {
  return items.map((item, index) => ({
    key: item.value,
    percentage: item.percentage,
    color: colors[index % colors.length],
    tooltip: `${tooltipPrefix} ${item.value} · ${item.percentage.toFixed(0)}%`,
  }));
}

export { TestingCoverageCard };
